import { MetaAdAccount, MetaAdCampaign, MetaAdSet } from '@frontend/gql/social/graphql';
import { CreateMetaAdCampaignMutation, CreateMetaAdSetMutation } from '@frontend/gql/social/graphql';
import { OptimisticStateService, OptimisticStateDependencies, OptimisticStateContext } from '../types/services';

/**
 * Creates an optimistic state service that handles optimistic updates
 * to raw MetaAdAccount[] GraphQL data structure
 */
export const createOptimisticStateService = (deps: OptimisticStateDependencies): OptimisticStateService => {
  const updateAdAccounts = (
    context: OptimisticStateContext,
    campaignData?: CreateMetaAdCampaignMutation['createMetaAdCampaign'],
    adSetData?: CreateMetaAdSetMutation['createMetaAdSet'],
    createdCampaignId?: string,
  ): void => {
    deps.logger.info('Applying optimistic update:', {
      adAccountId: context.adAccountId,
      campaignType: context.campaignType,
      adSetType: context.adSetType,
      hasCampaignData: !!campaignData,
      hasAdSetData: !!adSetData,
      createdCampaignId,
    });

    // Use functional state update to access current optimistic state
    deps.setOptimisticMetaAdAccounts((prevOptimisticState) => {
      try {
        // Use optimistic state if available, otherwise fall back to current accounts
        const currentAccounts = prevOptimisticState ?? deps.getCurrentAdAccounts();

        if (!currentAccounts) {
          deps.logger.warn('No current ad accounts available for optimistic update');
          return prevOptimisticState;
        }

        const targetAccountIndex = currentAccounts.findIndex((account) => account.id === context.adAccountId);

        if (targetAccountIndex === -1) {
          deps.logger.warn(`Ad account ${context.adAccountId} not found for optimistic update`);
          return prevOptimisticState;
        }

        const updatedAccounts = [...currentAccounts];
        const targetAccount = { ...updatedAccounts[targetAccountIndex] };

        // Update campaigns if we have campaign data
        if (campaignData && context.campaignType === 'new') {
          const optimisticCampaign: MetaAdCampaign = {
            __typename: 'MetaAdCampaign',
            id: campaignData.id,
            name: campaignData.name,
            objective: campaignData.objective,
            status: campaignData.status,
            adAccountId: campaignData.adAccountId,
            startTime: campaignData.createdAt || new Date().toISOString(),
            stopTime: null,
            createdAt: campaignData.createdAt || new Date().toISOString(),
            updatedAt: campaignData.updatedAt || new Date().toISOString(),
            metaAdSets: [],
          };

          targetAccount.metaAdCampaigns = [...(targetAccount.metaAdCampaigns || []), optimisticCampaign];

          deps.logger.info('Added optimistic campaign:', {
            campaignId: campaignData.id,
            campaignName: campaignData.name,
          });
        }

        // Update ad sets if we have ad set data
        if (adSetData && context.adSetType === 'new') {
          // Since the mutation doesn't return adCampaignId, we need to get it from context
          const targetCampaignId =
            createdCampaignId ||
            (context.formValues.campaign.type === 'existing' ? context.formValues.campaign.campaignId : undefined);

          if (!targetCampaignId) {
            deps.logger.warn('No campaign ID available for ad set optimistic update');
            return prevOptimisticState;
          }

          const optimisticAdSet: MetaAdSet = {
            __typename: 'MetaAdSet',
            id: adSetData.id,
            name: adSetData.name,
            status: adSetData.status || 'ACTIVE',
            adAccountId: adSetData.adAccountId,
            adCampaignId: targetCampaignId, // Use the campaign ID from context
            startTime: adSetData.startTime || new Date().toISOString(),
            stopTime: adSetData.stopTime || null,
            dailyBudget: adSetData.dailyBudget || null,
            lifetimeBudget: adSetData.lifetimeBudget || null,
            billingEvent: adSetData.billingEvent || null,
            bidAmount: adSetData.bidAmount || null,
            createdAt: adSetData.createdAt || new Date().toISOString(),
            updatedAt: adSetData.updatedAt || new Date().toISOString(),
            metaAds: [],
          };

          if (targetAccount.metaAdCampaigns) {
            const targetCampaignIndex = targetAccount.metaAdCampaigns.findIndex(
              (campaign) => campaign?.id === targetCampaignId,
            );

            if (targetCampaignIndex !== -1 && targetAccount.metaAdCampaigns[targetCampaignIndex]) {
              const updatedCampaigns = [...targetAccount.metaAdCampaigns];
              const existingCampaign = updatedCampaigns[targetCampaignIndex];

              if (existingCampaign) {
                updatedCampaigns[targetCampaignIndex] = {
                  ...existingCampaign,
                  metaAdSets: [...(existingCampaign.metaAdSets || []), optimisticAdSet],
                };
                targetAccount.metaAdCampaigns = updatedCampaigns;

                deps.logger.info('Added optimistic ad set:', {
                  adSetId: adSetData.id,
                  adSetName: adSetData.name,
                  campaignId: targetCampaignId,
                });
              }
            } else {
              deps.logger.warn(`Target campaign ${targetCampaignId} not found for ad set optimistic update`);
              deps.logger.info('Available campaigns:', {
                campaigns: targetAccount.metaAdCampaigns?.map((c) => ({ id: c?.id, name: c?.name })),
              });
              return prevOptimisticState;
            }
          } else {
            deps.logger.warn('No campaigns available in target account for ad set optimistic update');
            return prevOptimisticState;
          }
        }

        // Apply the updated account
        updatedAccounts[targetAccountIndex] = targetAccount;

        deps.logger.info('Optimistic state update completed successfully');
        return updatedAccounts;
      } catch (error) {
        deps.logger.error('Error in optimistic state update:', error);
        return prevOptimisticState;
      }
    });
  };

  const resetOptimisticState = (): void => {
    deps.logger.info('🔍 DEBUG - Resetting optimistic state');
    deps.setOptimisticMetaAdAccounts(undefined);
  };

  const getEffectiveAdAccounts = (): MetaAdAccount[] | undefined => {
    return deps.getCurrentAdAccounts();
  };

  return {
    updateAdAccounts,
    resetOptimisticState,
    getEffectiveAdAccounts,
  };
};
